// Step configurations for HowItWorksFlow, mirroring the backend pipelines 1:1
// so the animation's rail number + name match what Azure Functions reports at
// every tick. Order/labels track DocumentProcessingStageMapper (StatusBanner's
// STAGE_STEPS) and the DiscoveryStep enum (DiscoveryProgressBar's STEP_LABELS).

export type FlowVisual = 'invoice' | 'scan' | 'fields' | 'checks' | 'meters' | 'json';

export interface FlowStep {
  /** Short label for the progress rail. */
  rail: string;
  /** Headline for the active beat. */
  h2: string;
  /** Short description under the headline. */
  p: string;
  /** Card badge. */
  badge: { t: string; tone: '' | 'run' | 'ok' };
  /** Which visual state to show. */
  visual: FlowVisual;
}

/** Document pipeline — one entry per backend `currentStage`, in rank order. */
export const DOCUMENT_STEPS: ReadonlyArray<FlowStep> = [
  {
    rail: 'En cola',
    h2: 'En cola',
    p: 'Esperando a que el motor lo procese.',
    badge: { t: 'en cola', tone: '' },
    visual: 'invoice',
  },
  {
    rail: 'Preparación',
    h2: 'Preparando el documento',
    p: 'Resolviendo la ruta de extracción y el recurso de lectura.',
    badge: { t: 'preparando', tone: 'run' },
    visual: 'invoice',
  },
  {
    rail: 'Lectura (OCR)',
    h2: 'Leyendo el documento',
    p: 'OCR: detecta texto, tablas, importes, fechas y zonas importantes.',
    badge: { t: 'OCR · leyendo', tone: 'run' },
    visual: 'scan',
  },
  {
    rail: 'Extracción (IA)',
    h2: 'Extrayendo los campos',
    p: 'La IA estructura el documento en datos entendibles.',
    badge: { t: 'IA · estructurando', tone: 'run' },
    visual: 'fields',
  },
  {
    rail: 'Validación',
    h2: 'Validando los datos',
    p: 'Comprueba coherencias, formatos y reglas de negocio.',
    badge: { t: 'validando', tone: 'run' },
    visual: 'checks',
  },
  {
    rail: 'Finalización',
    h2: 'Puntuando y guardando',
    p: 'Calcula la confianza por campo y guarda el resultado.',
    badge: { t: 'scoring', tone: 'run' },
    visual: 'meters',
  },
];

/**
 * The extra "Clasificando" beat shown ONLY for auto-classified jobs, between «En cola» and
 * «Preparación». Manual uploads never see it — they keep {@link DOCUMENT_STEPS} exactly.
 */
export const CLASSIFYING_STEP: FlowStep = {
  rail: 'Clasificando',
  h2: 'Clasificando el documento',
  p: 'Identificando el tipo de documento.',
  badge: { t: 'clasificando', tone: 'run' },
  visual: 'scan',
};

/**
 * Document pipeline for an auto-classified job: {@link DOCUMENT_STEPS} with the «Clasificando» beat
 * inserted at index 1 (every later stage shifts up by one). Consumed by {@link ProcessingShowcase}
 * when the reviewed job was auto-classified so the rail count matches StatusBanner's «Paso N/7».
 */
export const DOCUMENT_STEPS_CLASSIFIED: ReadonlyArray<FlowStep> = [
  DOCUMENT_STEPS[0]!,
  CLASSIFYING_STEP,
  ...DOCUMENT_STEPS.slice(1),
];

/** Discovery pipeline — one entry per in-flight `DiscoveryStep`, in declared order. */
export const DISCOVERY_STEPS: ReadonlyArray<FlowStep> = [
  {
    rail: 'En cola',
    h2: 'En cola',
    p: 'El descubrimiento está encolado.',
    badge: { t: 'en cola', tone: '' },
    visual: 'invoice',
  },
  {
    rail: 'Lectura (OCR)',
    h2: 'Extrayendo OCR de las muestras',
    p: 'Lee el texto de cada documento de ejemplo.',
    badge: { t: 'OCR · leyendo', tone: 'run' },
    visual: 'scan',
  },
  {
    rail: 'Análisis (IA)',
    h2: 'Analizando campos candidatos',
    p: 'La IA propone qué datos debería capturar el tipo de documento.',
    badge: { t: 'IA · analizando', tone: 'run' },
    visual: 'fields',
  },
  {
    rail: 'Madurez',
    h2: 'Calculando la madurez',
    p: 'Estima si el tipo está listo para automatizar.',
    badge: { t: 'madurez', tone: 'run' },
    visual: 'meters',
  },
  {
    rail: 'Recomendaciones',
    h2: 'Generando recomendaciones',
    p: 'Reglas deterministas que afinan la propuesta.',
    badge: { t: 'recomendando', tone: 'run' },
    visual: 'checks',
  },
  {
    rail: 'Guardado',
    h2: 'Guardando la propuesta',
    p: 'Persiste el borrador con los campos propuestos.',
    badge: { t: 'guardando', tone: 'run' },
    visual: 'json',
  },
];
